import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useDepartmentColor } from "@/hooks/useDepartmentColor";
import { useGetDepartmentDisplayName } from "@/hooks/useDepartmentDisplayName";
import { usePickShift } from "@/hooks/usePickShift";
import { ShiftTypeIcon } from "@/pages/MasterSchedule/v2/auxillary/ShiftTypeIcon";
import { localStorageService } from "@/services/LocalStorageService";
import { InHouseMobileSheet } from "@/ui/Popups/InHouseMobileSheet";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { OpenShift, ShiftType } from "@/utils/models";
import { deterministicPercent } from "@/utils/utilFunctions";
import { useSuggestedShifts } from "./useSuggestedShifts";

const PICKUP_SKIP_THRESHOLD = 33; // 33% chance to skip showing per day
const FORCE_SHOW_DAY = 6; // Force show on 7th day of week (Saturday, 0-indexed from Sunday)

/** Deterministic per-date decision: 67% chance to show, 33% to skip */
function shouldShowToday(userId: string, dateStr: string): boolean {
	return deterministicPercent(`${userId}-${dateStr}`) >= PICKUP_SKIP_THRESHOLD;
}

export interface SuggestedShift {
	id: string;
	startTime: DateTime;
	endTime: DateTime;
	shiftType: ShiftType;
	departmentId: string;
	location?: string;
	bonus?: number;
	isMerged?: boolean;
	originalShifts?: OpenShift[];
}

interface PickupSuggestionDrawerProps {
	onPeekVisibleChange?: (visible: boolean) => void;
}

export const PickupSuggestionDrawer: FC<PickupSuggestionDrawerProps> = ({ onPeekVisibleChange }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const getDepartmentDisplayName = useGetDepartmentDisplayName();
	const departmentColor = useDepartmentColor();
	const { showSnackbar } = useSnackbar();
	const today = DateTime.now().toFormat("yyyy-MM-dd");

	const persistedPinnedIds = useMemo(() => {
		// Read persisted pinned shift IDs so the same suggestions survive page refresh
		if (!userId) return undefined;
		const state = localStorageService.getPickupSuggestionState(userId);
		return state?.pinnedShiftIds;
	}, [userId]);

	const { data: latestSuggestedShifts } = useSuggestedShifts(persistedPinnedIds);
	const [suggestedShifts, setSuggestedShifts] = useState<SuggestedShift[]>(latestSuggestedShifts);
	const { pickShift, isPicking } = usePickShift({ refreshUponSuccess: true });
	const [currentShiftIdPicked, setCurrentShiftIdPicked] = useState<string | null>(null);

	useEffect(() => {
		// Only pin suggestions once from the first non-empty result.
		// After applying, the query refreshes but we keep showing the original suggestions.
		if (suggestedShifts.length === 0 && latestSuggestedShifts.length > 0) {
			setSuggestedShifts(latestSuggestedShifts);

			// Persist pinned IDs on first pin so they survive page refresh
			if (!persistedPinnedIds?.length && userId) {
				const currentState = localStorageService.getPickupSuggestionState(userId);
				if (currentState) {
					localStorageService.setPickupSuggestionState({ ...currentState, pinnedShiftIds: latestSuggestedShifts.map((s) => s.id) }, userId);
				}
			}
		}
	}, [latestSuggestedShifts, suggestedShifts.length, persistedPinnedIds, userId]);

	/**
	 * Weekly cycle logic:
	 * - "shouldRender": whether to mount the component at all
	 * - "initialOpen": whether the drawer starts opened vs minimized (peek)
	 *
	 * Each calendar week, pick a random day to show (67% chance per day, guaranteed by Saturday).
	 * Once shown: if dismissed same day → keep showing minimized → if quick applied, hide entirely →
	 * after that day, hide until next week.
	 */
	const { shouldRender, initialOpen } = useMemo(() => {
		if (!userId) return { shouldRender: false, initialOpen: false };

		const state = localStorageService.getPickupSuggestionState(userId);
		const now = DateTime.now();
		const currentWeekStart = now.startOf("week");

		if (!state) {
			// First time ever — always show immediately
			localStorageService.setPickupSuggestionState({ lastShownDate: today, dismissed: false, quickApplied: false, pickedCount: 0 }, userId);
			return { shouldRender: true, initialOpen: true };
		}

		const lastShown = DateTime.fromFormat(state.lastShownDate, "yyyy-MM-dd");
		const isNewWeek = lastShown.startOf("week") < currentWeekStart;

		if (isNewWeek) {
			const daysSinceWeekStart = Math.floor(now.diff(currentWeekStart, "days").days);

			// 7th day of the week (Saturday) — force show if not shown yet this week
			if (daysSinceWeekStart >= FORCE_SHOW_DAY || shouldShowToday(userId, today)) {
				localStorageService.setPickupSuggestionState({ lastShownDate: today, dismissed: false, quickApplied: false, pickedCount: 0 }, userId);
				return { shouldRender: true, initialOpen: true };
			}

			// Random check said skip today — don't render
			return { shouldRender: false, initialOpen: false };
		}

		// --- Same week as last shown ---

		if (state.lastShownDate === today) {
			// Same day as the weekly show
			if (state.quickApplied) return { shouldRender: false, initialOpen: false };
			if (state.dismissed) return { shouldRender: true, initialOpen: false };
			return { shouldRender: true, initialOpen: true };
		}

		// Different day, same week, already shown this week — don't render
		return { shouldRender: false, initialOpen: false };
	}, [userId, today]);

	const [showPickupSuggestion, setShowPickupSuggestion] = useState<boolean>(initialOpen);
	const [appliedShiftIds, setAppliedShiftIds] = useState<Set<string>>(() => {
		if (!userId) return new Set();
		const state = localStorageService.getPickupSuggestionState(userId);
		return new Set(state?.appliedShiftIds ?? []);
	});

	const hasVisibleShifts = suggestedShifts.length > 0;

	const isPeekVisible = shouldRender && hasVisibleShifts && !showPickupSuggestion;
	useEffect(() => {
		onPeekVisibleChange?.(isPeekVisible);
	}, [isPeekVisible, onPeekVisibleChange]);

	useEffect(() => {
		if (showPickupSuggestion && hasVisibleShifts) {
			track("pickup_suggestion_shown", {
				shift_ids: JSON.stringify(suggestedShifts.map((s) => s.id)),
				shift_count: suggestedShifts.length,
			});
		}
	}, [showPickupSuggestion, hasVisibleShifts, suggestedShifts, track]);

	const handleQuickApply = async (shift: SuggestedShift, shiftIndex: number) => {
		track("pickup_suggestion_quick_apply_clicked", {
			shift_id: shift.id,
			shift_type: shift.shiftType,
			shift_date: shift.startTime.toFormat("yyyy-MM-dd"),
			department_id: shift.departmentId,
			shift_index: shiftIndex,
		});

		try {
			setCurrentShiftIdPicked(shift.id);
			await pickShift({
				id: shift.id,
				departmentId: shift.departmentId,
				startTime: shift.startTime,
				endTime: shift.endTime,
				isMerged: shift.isMerged,
				originalShifts: shift.originalShifts,
			});

			track("pickup_suggestion_quick_apply_success", {
				shift_id: shift.id,
				shift_index: shiftIndex,
			});
			showSnackbar(t("pickup.suggestion.quick.apply.success"), "success");
		} catch (error) {
			track("pickup_suggestion_quick_apply_error", {
				shift_id: shift.id,
				shift_index: shiftIndex,
				error_message: error instanceof Error ? error.message : "unknown",
			});
			setCurrentShiftIdPicked(null);
			return;
		}

		setCurrentShiftIdPicked(null);
		const newApplied = new Set(appliedShiftIds).add(shift.id);
		setAppliedShiftIds(newApplied);

		// Update picked count and applied IDs in localStorage (preserve pinnedShiftIds)
		if (userId) {
			const currentState = localStorageService.getPickupSuggestionState(userId);
			const newPickedCount = (currentState?.pickedCount ?? 0) + 1;
			localStorageService.setPickupSuggestionState(
				{ ...currentState, lastShownDate: today, dismissed: false, quickApplied: false, pickedCount: newPickedCount, appliedShiftIds: [...newApplied] },
				userId,
			);
		}
	};

	const handleSeeMoreOpportunities = () => {
		track("pickup_suggestion_see_more_clicked", {
			shift_ids: JSON.stringify(suggestedShifts.map((s) => s.id)),
		});
		navigate("/open-shifts");
		setShowPickupSuggestion(false);
		if (userId) {
			const currentState = localStorageService.getPickupSuggestionState(userId);
			localStorageService.setPickupSuggestionState({ ...currentState, lastShownDate: today, dismissed: true, quickApplied: false }, userId);
		}
	};

	const handleClose = () => {
		track("pickup_suggestion_dismissed", {
			shift_ids: JSON.stringify(suggestedShifts.map((s) => s.id)),
		});
		setShowPickupSuggestion(false);
		if (userId) {
			const currentState = localStorageService.getPickupSuggestionState(userId);
			localStorageService.setPickupSuggestionState({ ...currentState, lastShownDate: today, dismissed: true, quickApplied: false }, userId);
		}
	};

	const handleOpen = useCallback(() => {
		setShowPickupSuggestion(true);
	}, []);

	if (!shouldRender || !hasVisibleShifts) {
		return null;
	}

	return (
		<InHouseMobileSheet
			open={showPickupSuggestion}
			onClose={handleClose}
			onOpen={handleOpen}
			title={t("pickup.suggestion.title")}
			showHeader
			showHandle
			peekWhenClosed
			height="auto"
			paperSx={{ pb: 2 }}
			footer={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<SystemButton buttonVariant="secondary" onClick={handleSeeMoreOpportunities} sx={{ width: "100%" }}>
						{t("pickup.suggestion.see.more")}
					</SystemButton>
				</Box>
			}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{suggestedShifts.map((shift, index) => (
					<Box
						key={shift.id}
						sx={{
							bgcolor: "#F8F9FB",
							borderRadius: "12px",
							p: 2,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
							<Box
								sx={{
									width: "24px",
									height: "24px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<ShiftTypeIcon shiftType={shift.shiftType} />
							</Box>

							<Box sx={{ flex: 1 }}>
								<Typography
									variant="subtitle2"
									sx={{
										color: "primary.main",
										fontSize: "0.875rem",
										fontWeight: 600,
										mb: 1,
									}}
								>
									{shift.startTime.toFormat("ccc, MMM d")}
								</Typography>

								<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
									<Box
										sx={{
											bgcolor: departmentColor(shift.departmentId).dot,
											width: "4px",
											height: "40px",
											borderRadius: "2px",
											mr: 1,
										}}
									/>
									<Box>
										<Typography
											variant="body2"
											sx={{
												color: (theme) => alpha(theme.palette.primary.dark, 0.5),
												fontSize: "0.875rem",
												fontWeight: 600,
											}}
										>
											{getDepartmentDisplayName(shift.departmentId)}
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: (theme) => alpha(theme.palette.primary.dark, 0.5),
												fontSize: "0.875rem",
												fontWeight: 600,
											}}
										>
											{shift.startTime.toFormat("h:mm a")} - {shift.endTime.toFormat("h:mm a")}
										</Typography>
									</Box>
								</Box>

								{shift.location && (
									<Typography
										variant="body2"
										sx={{
											color: (theme) => alpha(theme.palette.primary.dark, 0.5),
											fontSize: "0.775rem",
											ml: 1.5,
										}}
									>
										{shift.location}
									</Typography>
								)}

								{!!shift.bonus && (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											mt: 1,
											ml: 1.5,
										}}
									>
										<img src="/bonus.svg" alt="Bonus" />
										<Typography sx={{ color: "primary.dark", fontWeight: 600 }}>+${shift.bonus}</Typography>
									</Box>
								)}
							</Box>
						</Box>

						{appliedShiftIds.has(shift.id) ? (
							<SystemButton onClick={(_) => {}} disabled buttonVariant="secondary" sx={{ width: "100%", mt: 2 }}>
								{t("pickup.suggestion.pending")}
							</SystemButton>
						) : (
							<SystemButton
								onClick={() => handleQuickApply(shift, index)}
								loading={isPicking && currentShiftIdPicked === shift.id}
								disabled={isPicking && currentShiftIdPicked !== shift.id}
								sx={{ width: "100%", mt: 2 }}
							>
								{t("pickup.suggestion.quick.apply")}
							</SystemButton>
						)}
					</Box>
				))}
			</Box>
		</InHouseMobileSheet>
	);
};
